import { createContext, useState } from 'react';
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getStoredUser,
  setStoredUser,
} from '../utils/auth';

// Web counterpart of the mobile app's auth-context (see big-ball-mobile
// src/store/auth-context.js). It holds the session token and the current user
// in React state, and mirrors both to localStorage through src/utils/auth.js.
//
// localStorage (not just React state) is the source of truth because React
// Router loaders, actions and the repo layer run outside the React tree and
// cannot read context; they read the token via src/utils/auth.js, which is
// backed by the same storage this provider writes to.
const AuthContext = createContext({
  token: null,
  currentUser: null,
  isAuthenticated: false,
  authenticate: (token, user) => {},
  logout: () => {},
});

function AuthContextProvider({ children }) {
  // Seed state synchronously from storage so the token is available on the very
  // first render (localStorage is synchronous). This avoids a logged-out flash
  // that would wrongly redirect an authenticated user on reload.
  const [authToken, setToken] = useState(() => getAuthToken());
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());

  function authenticate(token, user) {
    setAuthToken(token);
    setStoredUser(user);
    setToken(token);
    setCurrentUser(user);
  }

  function logout() {
    removeAuthToken();
    setToken(null);
    setCurrentUser(null);
  }

  const value = {
    token: authToken,
    currentUser,
    isAuthenticated: !!authToken,
    authenticate,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthContextProvider };
